import { useState, useEffect } from "react";
import { H1Title } from "../../components/typography/typography";
import { AppButton } from "../../components/buttons/button";
import AuthGeneralInput from "../../components/input/authinput";
import NotificationService from "../../services/notificationService";
import SubmissionService from "../../services/submissionService";
import CompetitionService from "../../services/competitionService";
import { Success, Error as ToastError } from "../../components/toast/toast";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPaperPlane, faUsers, faHistory, faEnvelope, faCheckCircle } from "@fortawesome/free-solid-svg-icons";
import api from "../../services/api";

export default function AdminNotifications() {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [recipientGroup, setRecipientGroup] = useState('all_participants');
  const [sending, setSending] = useState(false);
  const [participants, setParticipants] = useState([]);
  const [competitionId, setCompetitionId] = useState(null);
  const [sentNotifications, setSentNotifications] = useState([]);
  const [loadingSent, setLoadingSent] = useState(true);

  useEffect(() => { loadData(); loadSentHistory(); }, []);

  const loadData = async () => {
    try {
      const { data: compData } = await CompetitionService.getActive();
      setCompetitionId(compData.data.competition._id);
    } catch { /* */ }
  };

  const loadSentHistory = async () => {
    setLoadingSent(true);
    try {
      const { data } = await NotificationService.getAllSent({ limit: 50 });
      setSentNotifications(data.data.notifications || []);
    } catch { /* */ }
    finally { setLoadingSent(false); }
  };

  const getRecipientIds = async () => {
    try {
      if (recipientGroup === 'all_participants') {
        const { data } = await api.get('/admin/users', { params: { role: 'participant' } });
        return (data.data.users || []).map(u => u._id);
      }
      if (recipientGroup === 'all_judges') {
        const { data } = await api.get('/admin/users', { params: { role: 'judge' } });
        return (data.data.users || []).map(u => u._id);
      }
      if (recipientGroup === 'submitted' && competitionId) {
        const { data } = await SubmissionService.getAll({ competitionId, status: 'submitted', limit: 500 });
        return (data.data.submissions || []).map(s => s.submitterId?._id).filter(Boolean);
      }
      if (recipientGroup === 'advanced' && competitionId) {
        const { data } = await SubmissionService.getAll({ competitionId, status: 'advanced', limit: 500 });
        return (data.data.submissions || []).map(s => s.submitterId?._id).filter(Boolean);
      }
      return [];
    } catch {
      return [];
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!title.trim() || !body.trim()) { ToastError('Title and body required'); return; }

    setSending(true);
    try {
      const recipientIds = await getRecipientIds();
      if (recipientIds.length === 0) { ToastError('No recipients found for this group'); setSending(false); return; }

      await NotificationService.sendBulk({ recipientIds, type: 'general', title, body });
      Success(`Notification sent to ${recipientIds.length} recipients`);
      setTitle('');
      setBody('');
      loadSentHistory();
    } catch (err) {
      ToastError(err.response?.data?.error?.message || 'Failed to send');
    } finally { setSending(false); }
  };

  return (
    <div className="space-y-6 max-w-2xl">
      <H1Title size="sm">Send Notification</H1Title>

      <form onSubmit={handleSend} className="bg-white rounded-xl border border-grey-200 p-6 space-y-4">
        {/* Recipient Group */}
        <div>
          <label className="block text-sm font-semibold mb-2">
            <FontAwesomeIcon icon={faUsers} className="mr-1 text-primary" /> Recipients
          </label>
          <select
            value={recipientGroup}
            onChange={(e) => setRecipientGroup(e.target.value)}
            className="w-full border border-grey-200 rounded-md px-4 py-3 text-sm outline-none focus:border-primary"
          >
            <option value="all_participants">All Participants</option>
            <option value="all_judges">All Judges</option>
            <option value="submitted">Participants with Submitted Proposals</option>
            <option value="advanced">Advanced Participants Only</option>
          </select>
        </div>

        <AuthGeneralInput
          label="Notification Title"
          name="title"
          placeholder="e.g. Round 1 Results Are In"
          value={title}
          change={(e) => setTitle(e.target.value)}
        />

        <div>
          <label className="block text-sm font-semibold mb-2">Message Body</label>
          <textarea
            className="w-full border border-grey-200 rounded-md p-4 text-sm min-h-[150px] outline-none focus:border-primary resize-y"
            placeholder="Write your notification message here..."
            value={body}
            onChange={(e) => setBody(e.target.value)}
          />
        </div>

        <AppButton type="submit" loading={sending}>
          <FontAwesomeIcon icon={faPaperPlane} className="mr-2" /> Send Notification
        </AppButton>
      </form>

      {/* Sent History */}
      <div className="bg-white rounded-xl border border-grey-200 p-5">
        <h3 className="font-bold mb-4">
          <FontAwesomeIcon icon={faHistory} className="mr-2 text-primary" /> Sent Notifications ({sentNotifications.length})
        </h3>
        {loadingSent ? (
          <p className="text-sm text-grey-667 text-center py-6">Loading...</p>
        ) : sentNotifications.length === 0 ? (
          <p className="text-sm text-grey-667 text-center py-6">No notifications sent yet</p>
        ) : (
          <div className="divide-y divide-grey-200 max-h-[400px] overflow-y-auto">
            {sentNotifications.map((n) => (
              <div key={n._id} className="py-3 flex items-start gap-3">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${n.isRead ? 'bg-green-light' : 'bg-primary-bg'}`}>
                  <FontAwesomeIcon icon={n.isRead ? faCheckCircle : faEnvelope} className={`text-xs ${n.isRead ? 'text-green' : 'text-primary'}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-semibold truncate">{n.title}</p>
                    <span className="text-xs text-grey-98 flex-shrink-0 ml-2">{new Date(n.createdAt).toLocaleDateString()}</span>
                  </div>
                  <p className="text-xs text-grey-667 truncate">{n.body}</p>
                  <p className="text-xs text-grey-98 mt-0.5">
                    To: {n.recipientId?.firstName} {n.recipientId?.lastName} ({n.recipientId?.email})
                    {n.isRead && <span className="text-green ml-2">Read</span>}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Templates */}
      <div className="bg-white rounded-xl border border-grey-200 p-5">
        <h3 className="font-bold mb-3">Quick Templates</h3>
        <div className="space-y-2">
          {[
            { t: 'Round 1 Results', b: 'Thank you for your submission to The Human Development Lab. Results for Round 1 have been posted. Please log in to your portal to see your status.' },
            { t: 'Video Pitch Reminder', b: 'This is a reminder that your recorded pitch is due by the end of this month. Please upload your video link through the portal.' },
            { t: 'Fundraising Deadline', b: 'The fundraising deadline is approaching. Please ensure your fundraising sources are logged and verified in the portal.' },
            { t: 'Competition Update', b: 'We have an important update about The Human Development Lab. Please log in to your portal for details.' },
          ].map((tmpl, i) => (
            <button
              key={i}
              onClick={() => { setTitle(tmpl.t); setBody(tmpl.b); }}
              className="w-full text-left px-4 py-3 rounded-lg border border-grey-200 hover:bg-grey-bg text-sm cursor-pointer transition-colors"
            >
              <p className="font-semibold">{tmpl.t}</p>
              <p className="text-xs text-grey-98 truncate">{tmpl.b}</p>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
